/**
 * The judgement. Everything else on this wire is plumbing; this file is what
 * decides whether the person holding the other phone is who the QR says.
 *
 * It runs in a corridor with no network on either side, so there is nothing to
 * ask. Every answer comes from the bytes in hand plus one number the device
 * already knew: the corrected clock. When that number is unavailable the
 * honest answer is to refuse, not to guess.
 *
 * Reasons are stable English identifiers. The Spanish a verifier reads lives in
 * the screen layer; if a refusal here needs new words, the enum below is
 * missing a case rather than the string being missing a translation.
 */
import { ATTR, FRESHNESS_WINDOW_S, MODE_FAST, MODE_STRONG } from "./constants";
import { importPublicKey, verifyEd25519 } from "./crypto";
import type { Attributes } from "./format";
import type { StatusAnswer } from "./status";
import {
  decodeAttributes, decodeCredential, decodeErrand, decodePresentation, errandSigningInput,
  presentationSigningInput,
} from "./format";

export type VerifyReason =
  | "ok" | "malformed" | "clock-unsynced" | "unknown-issuer-key"
  | "bad-issuer-signature" | "credential-revoked" | "credential-expired"
  | "presentation-stale" | "presentation-future"
  | "bad-device-signature" | "nonce-replayed" | "unsupported-mode"
  | "challenge-unknown" | ErrandReason;

/** The refusals only a presentation carrying an errand in key 6 can get. */
export type ErrandReason =
  | "errand-malformed" | "errand-unknown-issuer-key" | "errand-bad-issuer-signature"
  | "errand-grantor-revoked" | "errand-grantor-expired" | "errand-bad-signature"
  | "errand-wrong-bearer" | "errand-expired";

/** What the verifier is shown under the bearer's identity: who authorised
 *  what, and until when. Read from the grantor's issuer-signed credential and
 *  the errand the grantor's device signed. */
export interface ErrandView {
  grantorId: string;
  grantorName: string;
  grantorRole: string;
  text: string;
  expiresAt: number;
}

export interface VerifyResult {
  ok: boolean;
  reason: VerifyReason;
  attributes?: Attributes;
  portraitUnverified?: boolean;
  /** On a fast-mode acceptance only. A strong-mode answer is as old as the
   *  verifier's own challenge, and its timestamp is not what was checked. */
  ageSeconds?: number;
  /** The mode of an accepted presentation, so the screen can say which. */
  mode?: number;
  /** What the status list said, on an accepted credential. */
  statusState?: StatusAnswer["state"];
  /** How old that list was, when there was one. */
  statusAgeSeconds?: number;
  /** Present when the presentation carried an errand and it verified. */
  errand?: ErrandView;
}

export interface VerifyOptions {
  /** kid -> raw 32-byte Ed25519 public key. */
  issuerPublicKeys: Map<number, Uint8Array>;
  /** Corrected clock, seconds. */
  now: number;
  /** False when this device has never learned the server's time. */
  clockSynced: boolean;
  /**
   * Nonces already accepted; mutated on success. Nothing here evicts from
   * it — the caller owns the set and is responsible for dropping entries
   * older than two freshness windows, which is where Task 7's storage layer
   * does it. A caller that never evicts grows this set without bound; one
   * that evicts sooner than two windows reopens the replay it exists for.
   */
  seenNonces: Set<string>;
  /**
   * Strong-mode challenges this verifier issued and is still waiting on, keyed
   * like `seenNonces`; the one a presentation answers is deleted on success.
   * Absent means this verifier is waiting on none, so every strong-mode
   * presentation is refused with `challenge-unknown`, which is what the spec
   * asks of a verifier that did not start the exchange.
   */
  pendingChallenges?: Set<string>;
  /**
   * What the stored status list says about a status index. Required, so a
   * caller cannot verify without deciding where revocation comes from.
   */
  status: (index: number) => StatusAnswer;
}

/**
 * A refusal carries the reason and nothing else. Handing back the decoded
 * attributes of a presentation we just rejected is how a name and a portrait
 * end up on a verifier's screen under a red banner nobody reads.
 */
const fail = (reason: VerifyReason): VerifyResult => ({ ok: false, reason });

export async function verifyPresentation(
  raw: Uint8Array, options: VerifyOptions,
): Promise<VerifyResult> {
  if (!options.clockSynced) return fail("clock-unsynced");

  let presentation, credential, attributes: Attributes;
  try {
    presentation = decodePresentation(raw);
    credential = decodeCredential(presentation.credentialBytes);
    attributes = decodeAttributes(credential.attributesBytes);
  } catch {
    // Every decoder in `format.ts` reports a bad wire the same way, by
    // throwing. A poster, a bus ticket and a truncated QR all arrive here, and
    // none of them may escape into the caller as an exception.
    return fail("malformed");
  }

  // Two modes. Any other value is a format this version cannot reason about,
  // and a replay story it does not know is not one to accept on trust.
  const strong = presentation.mode === MODE_STRONG;
  if (presentation.mode !== MODE_FAST && !strong) return fail("unsupported-mode");

  const issuerRaw = options.issuerPublicKeys.get(credential.kid);
  if (!issuerRaw) return fail("unknown-issuer-key");

  // A wrong-length entry in the trust store is a misconfigured app, not an
  // unknown issuer, but the verifier has one screen and no network to say so
  // on. Either way this kid yields no usable key.
  const issuerKey = await importPublicKey(issuerRaw);
  if (!issuerKey) return fail("unknown-issuer-key");
  if (!(await verifyEd25519(issuerKey, credential.signature, credential.attributesBytes))) {
    return fail("bad-issuer-signature");
  }

  // Read like the expiry below: an index that is absent or not a status index
  // would otherwise be a credential no revocation can reach.
  const index = attributes.get(ATTR.STATUS_INDEX);
  if (!Number.isSafeInteger(index) || (index as number) < 0) return fail("malformed");
  // After the issuer signature, because an index only belongs to this
  // credential once the UH has signed it. Before expiry and freshness, because
  // revoked is the one refusal that a renewal or a new code cannot fix, so it
  // is the one the verifier should be told.
  const status = options.status(index as number);
  if (status.state === "revoked") return fail("credential-revoked");

  // Read the expiry the way `format.ts` reads every other field. Coercing it
  // with `Number()` would turn an absent attribute 8 into NaN, and `NaN <= now`
  // is false, so a credential with no expiry date would verify forever.
  const expiresAt = attributes.get(ATTR.EXPIRES_AT);
  if (typeof expiresAt !== "number" || !Number.isInteger(expiresAt)) return fail("malformed");
  if (expiresAt <= options.now) return fail("credential-expired");

  // The errand's checks sit where the original spec puts them: after both
  // credentials, before the freshness of the presentation. The bearer's device
  // signature below covers the errand bytes, so an errand lifted out of one
  // presentation cannot be put into another.
  let errand: ErrandView | undefined;
  if (presentation.errandBytes !== undefined) {
    const checked = await verifyErrand(presentation.errandBytes, {
      issuerPublicKeys: options.issuerPublicKeys,
      now: options.now,
      status: options.status,
      bearer: attributes.get(ATTR.ID),
    });
    if (!checked.ok) return fail(checked.reason);
    errand = checked.errand;
  }

  // The window is two-sided. A one-sided one accepts anything dated ahead of
  // now, which is a phone with its clock wound forward generating a year of
  // presentations this afternoon.
  //
  // Not in strong mode. There the verifier's own challenge, alive for two
  // minutes on the verifier's clock, is what makes the answer fresh, and not
  // asking the holder's clock is the half of what the mode buys that the spec
  // names: no dependence on the two clocks agreeing.
  const age = options.now - presentation.timestamp;
  if (!strong) {
    if (age > FRESHNESS_WINDOW_S) return fail("presentation-stale");
    if (age < -FRESHNESS_WINDOW_S) return fail("presentation-future");
  }

  // The device key comes from the issuer-signed attributes, never from the
  // presentation. That is what stops a valid signature over someone else's
  // credential.
  // Checked rather than asserted: `as Uint8Array` would let a 32-character
  // string reach `importKey`, which throws on it. The length is not checked
  // here — `importPublicKey` owns that for both keys.
  const devicePublicRaw = attributes.get(ATTR.DEVICE_KEY);
  if (!(devicePublicRaw instanceof Uint8Array)) return fail("malformed");
  const deviceKey = await importPublicKey(devicePublicRaw);
  if (!deviceKey) return fail("malformed");
  const signingInput = presentationSigningInput({
    credentialBytes: presentation.credentialBytes,
    timestamp: presentation.timestamp,
    nonce: presentation.nonce,
    mode: presentation.mode,
    errandBytes: presentation.errandBytes,
  });
  if (!(await verifyEd25519(deviceKey, presentation.signature, signingInput))) {
    return fail("bad-device-signature");
  }

  // The seen-set stores the nonce, never a hash of the raw presentation. The
  // device signature covers the fields rebuilt above, not the envelope bytes
  // that carried them, so the same signed presentation can be re-encoded into
  // different bytes and a byte-hash would see a new one each time. Do not
  // optimise this into a digest.
  //
  // Recorded last, after every other check has passed: a rejected presentation
  // must not burn the nonce of a later honest one.
  //
  // In strong mode the same place, for the same reason: a smudged or tampered
  // answer must not spend the challenge the holder's honest answer needs. The
  // pending set replaces the seen-set there rather than joining it, because
  // deleting the challenge is already what stops the answer being replayed.
  const key = nonceKey(presentation.nonce);
  if (strong) {
    if (!options.pendingChallenges?.has(key)) return fail("challenge-unknown");
    options.pendingChallenges.delete(key);
  } else {
    if (options.seenNonces.has(key)) return fail("nonce-replayed");
    options.seenNonces.add(key);
  }

  return {
    ok: true,
    reason: "ok",
    attributes,
    // Attribute 10 is absent when the directory holds no photograph of this
    // person. That is a verification that succeeded with nothing to compare a
    // face against, not a failure.
    portraitUnverified: !attributes.has(ATTR.PORTRAIT),
    ...(strong ? {} : { ageSeconds: Math.max(0, age) }),
    mode: presentation.mode,
    statusState: status.state,
    statusAgeSeconds:
      status.listIssuedAt === undefined ? undefined : Math.max(0, options.now - status.listIssuedAt),
    ...(errand ? { errand } : {}),
  };
}

/**
 * How the seen-set and the pending challenges key a nonce. Exported so
 * `challenges.ts` stores exactly what this file looks up: a key the two spelled
 * differently would be a challenge no answer ever finds.
 */
export function nonceKey(nonce: Uint8Array): string {
  return Array.from(nonce).join(",");
}

export type ErrandCheck =
  | { ok: true; errand: ErrandView }
  | { ok: false; reason: ErrandReason };

/**
 * An errand on its own, in the original spec's order: the grantor's
 * credential signed by the UH, not revoked and not expired; the errand signed
 * by the device that credential names; the errand naming `bearer`; the errand
 * not expired.
 *
 * Two callers. `verifyPresentation` passes the presenter's attribute 1, and
 * the receiving wallet passes its own holder's, so an errand for someone else
 * is refused before it is stored rather than at the first verifier.
 *
 * `bearer` is `unknown` because it comes out of a decoded attribute map; a
 * non-string can match no errand, and is refused as the wrong bearer.
 */
export async function verifyErrand(
  raw: Uint8Array,
  options: Pick<VerifyOptions, "issuerPublicKeys" | "now" | "status"> & { bearer: unknown },
): Promise<ErrandCheck> {
  const refuse = (reason: ErrandReason): ErrandCheck => ({ ok: false, reason });

  let errand, credential, attributes: Attributes;
  try {
    errand = decodeErrand(raw);
    credential = decodeCredential(errand.credentialBytes);
    attributes = decodeAttributes(credential.attributesBytes);
  } catch {
    return refuse("errand-malformed");
  }

  const issuerKey = await importPublicKey(options.issuerPublicKeys.get(credential.kid) ?? new Uint8Array(0));
  if (!issuerKey) return refuse("errand-unknown-issuer-key");
  if (!(await verifyEd25519(issuerKey, credential.signature, credential.attributesBytes))) {
    return refuse("errand-bad-issuer-signature");
  }

  // The same reading, and the same place in the order, as the bearer's own
  // index and expiry above: a grantor dismissed yesterday authorises nothing
  // today, however good the bearer's identity is.
  const index = attributes.get(ATTR.STATUS_INDEX);
  if (!Number.isSafeInteger(index) || (index as number) < 0) return refuse("errand-malformed");
  if (options.status(index as number).state === "revoked") return refuse("errand-grantor-revoked");
  const grantorExpiresAt = attributes.get(ATTR.EXPIRES_AT);
  if (typeof grantorExpiresAt !== "number" || !Number.isInteger(grantorExpiresAt)) {
    return refuse("errand-malformed");
  }
  if (grantorExpiresAt <= options.now) return refuse("errand-grantor-expired");

  // The grantor's device key from the UH-signed attributes, never from the
  // errand, for the same reason as the bearer's.
  const deviceRaw = attributes.get(ATTR.DEVICE_KEY);
  if (!(deviceRaw instanceof Uint8Array)) return refuse("errand-malformed");
  const deviceKey = await importPublicKey(deviceRaw);
  if (!deviceKey) return refuse("errand-malformed");
  if (!(await verifyEd25519(deviceKey, errand.signature, errandSigningInput(errand)))) {
    return refuse("errand-bad-signature");
  }

  if (typeof options.bearer !== "string" || errand.bearer !== options.bearer) {
    return refuse("errand-wrong-bearer");
  }
  if (errand.expiresAt <= options.now) return refuse("errand-expired");

  const name = attributes.get(ATTR.NAME);
  const role = attributes.get(ATTR.ROLE);
  const id = attributes.get(ATTR.ID);
  return {
    ok: true,
    errand: {
      grantorId: typeof id === "string" ? id : "",
      grantorName: typeof name === "string" ? name : "",
      grantorRole: typeof role === "string" ? role : "",
      text: errand.text,
      expiresAt: errand.expiresAt,
    },
  };
}
